import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import { BeatLoader } from "react-spinners";
import SelectInput from "../common/SelectInput";
import tailwindClassnames from "../common/TailwindClassnames";
import { AttentionIcon } from "../common/ProjectIcons";
import { searchTaxonomy } from "../../api/taxonomyApi";
import { taxonLabel } from "./aiMappingsHelpers";
import { TaxonomyResponseDto } from "../common/ObjectTypes";

type TaxonSearchFn = (
  inputValue: string,
  callback: (results: TaxonomyResponseDto[]) => void,
) => void;

type TaxonOption = { value: number; label: string };

type ProjectTaxonPickerProps = {
  /** Currently selected taxon id, or null. */
  value: number | null;
  /** Called with the new selected taxon id (or null when cleared). */
  onChange: (id: number | null) => void;
  /** Label for the control. */
  name: string;
  /** Disable the control (view-only). */
  disabled?: boolean;
  /** Inline validation error. */
  errors?: string;
  /**
   * Known label for the current `value` (e.g. a pre-filled edit), so the
   * selected chip renders before the user searches. Optional.
   */
  initialLabel?: string | null;
  /**
   * Project-wide status per taxon_id ("in" the species list, or "excluded"),
   * covering the currently selected model's rows. Passing this (even an empty
   * Map) opts into the richer selected-species card + annotated inline search
   * results used by the AI Mappings dialogs; omit entirely to keep the
   * original compact dropdown.
   */
  taxonStatus?: Map<number, "in" | "excluded">;
  /** Rich mode only: caption above the selected-species card. */
  cardLabel?: string;
  /** Rich mode only: caption above the search input. */
  searchLabel?: string;
  /** Rich mode only: show the "excluded elsewhere" warning banner. Default
   *  true; set false for add-species-style flows where there's no per-row
   *  exclusion decision being made (nothing to warn about). */
  showExcludedWarning?: boolean;
  /** Rich mode only: reports the full taxonomy detail behind the current
   *  selection (or null once unknown/cleared), so the caller can use fields
   *  like scientific_name in its own copy. */
  onSelectedDetailChange?: (detail: TaxonomyResponseDto | null) => void;
};

/**
 * Single-select async taxon picker for the project AI-mappings and species-list
 * editors. Type-ahead over the global taxonomy (`/taxonomy/search`, ≥3 chars);
 * emits the numeric `taxonomy.id`. Mirrors `review/TaxonMultiSelect` but for a
 * single value, and does not offer the WI-Unknown option (project admins map
 * real taxa, not the unmapped-fallback filter the review screen needs).
 *
 * When `taxonStatus` is passed, renders a richer view (AKL-715 follow-up): a
 * selected-species card (name, rank badge, scientific name + authority,
 * taxonomic path) plus an inline, always-visible search-results list annotated
 * with each candidate's project status, instead of the plain `SelectInput`
 * dropdown.
 */
const ProjectTaxonPicker: React.FC<ProjectTaxonPickerProps> = ({
  value,
  onChange,
  name,
  disabled = false,
  errors = "",
  initialLabel = null,
  taxonStatus,
  cardLabel,
  searchLabel,
  showExcludedWarning = true,
  onSelectedDetailChange,
}) => {
  const { t } = useTranslation();
  // Cache id -> full taxonomy detail so the selected chip/card re-renders once
  // a label is learned, instead of freezing on "#id" (state, not a ref — a
  // ref mutation alone doesn't trigger the re-render that shows the result).
  const [detailCache, setDetailCache] = useState<
    Map<number, TaxonomyResponseDto>
  >(new Map());

  const cacheDetail = (tax: TaxonomyResponseDto) => {
    setDetailCache((prev) => {
      if (prev.get(tax.id) === tax) return prev;
      const next = new Map(prev);
      next.set(tax.id, tax);
      return next;
    });
  };

  // Seed a placeholder-only entry for a pre-filled value so the plain
  // SelectInput mode (no taxonStatus) shows its label immediately; rich mode
  // backfills the full detail separately below.
  useEffect(() => {
    if (value == null || !initialLabel) return;
    setDetailCache((prev) => {
      if (prev.has(value)) return prev;
      const next = new Map(prev);
      next.set(value, {
        id: value,
        common_name_english: initialLabel,
        scientific_name: null,
        species: null,
        authority: null,
        class_: "",
        order: null,
        family: null,
        genus: null,
        subfamily: null,
        suborder: null,
        subspecies: null,
        superfamily: null,
        taxon_level: "",
        taxonomy_sub_type: "",
        taxonomy_type: "",
        unique_identifier: "",
        iucn_category_id: null,
        reference_url: null,
        created_at: "",
        updated_at: "",
      });
      return next;
    });
  }, [value, initialLabel]);

  const rich = taxonStatus != null;

  // Debounced taxonomy search. Stable across renders so the timer persists
  // between keystrokes; cancelled on unmount below.
  const debouncedSearch = useMemo(
    () =>
      debounce<TaxonSearchFn>((inputValue, callback) => {
        searchTaxonomy(inputValue)
          .then((results) => {
            results.forEach(cacheDetail);
            callback(results);
          })
          .catch((error) => {
            console.error("Error searching taxonomy:", error);
            callback([]);
          });
      }, 300),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Rich mode only: best-effort backfill of the pre-filled value's full detail
  // (rank/authority/path) via a search-by-name lookup — there's no by-numeric-id
  // taxonomy endpoint. Degrades gracefully to the name-only placeholder above
  // if the lookup doesn't happen to surface it. Runs unconditionally (rather
  // than short-circuiting when detail is already cached) so the effect only
  // needs to depend on primitives, not the cache itself — this only fires
  // when the picker opens with a new pre-filled value, not on every render.
  useEffect(() => {
    if (!rich || value == null || !initialLabel) return;
    if (initialLabel.trim().length < 3) return;
    let cancelled = false;
    searchTaxonomy(initialLabel)
      .then((results) => {
        if (cancelled) return;
        const match = results.find((tax) => tax.id === value);
        if (!match) return;
        setDetailCache((prev) => {
          const next = new Map(prev);
          next.set(match.id, match);
          return next;
        });
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Error backfilling taxon detail:", error);
      });
    return () => {
      cancelled = true;
    };
  }, [rich, value, initialLabel]);

  const selectedDetail =
    value == null ? null : (detailCache.get(value) ?? null);

  useEffect(() => {
    onSelectedDetailChange?.(selectedDetail);
  }, [selectedDetail, onSelectedDetailChange]);

  if (!rich) {
    const loadOptions = (
      inputValue: string,
      callback: (options: TaxonOption[]) => void,
    ) => {
      const term = inputValue.trim();
      if (term.length < 3) {
        debouncedSearch.cancel();
        callback([]);
        return;
      }
      debouncedSearch(term, (results) =>
        callback(
          results.map((tax) => ({ value: tax.id, label: taxonLabel(tax) })),
        ),
      );
    };

    const selectedOption: TaxonOption | null =
      value == null
        ? null
        : {
            value,
            label: selectedDetail ? taxonLabel(selectedDetail) : `#${value}`,
          };

    return (
      <SelectInput
        options={[]}
        isAsync
        loadOptions={loadOptions}
        className="w-full rounded shadow text-left text-sm"
        isDisabled={disabled}
        isLoading={false}
        isClearable={true}
        isSearchable={true}
        isMulti={false}
        name={name}
        value={selectedOption}
        onChange={(selected: TaxonOption | null) =>
          onChange(selected ? selected.value : null)
        }
        errors={errors}
        searchBar={false}
        placeholder={t("Type to search species...")}
        noOptionsMessage={() => t("Type at least 3 characters to search")}
      />
    );
  }

  return (
    <RichTaxonPicker
      value={value}
      onChange={onChange}
      disabled={disabled}
      errors={errors}
      taxonStatus={taxonStatus as Map<number, "in" | "excluded">}
      selectedDetail={selectedDetail}
      debouncedSearch={debouncedSearch}
      cacheDetail={cacheDetail}
      cardLabel={cardLabel ?? t("Species this label resolves to")}
      searchLabel={searchLabel ?? t("Search the taxonomy")}
      showExcludedWarning={showExcludedWarning}
    />
  );
};

type RichTaxonPickerProps = {
  value: number | null;
  onChange: (id: number | null) => void;
  disabled: boolean;
  errors: string;
  taxonStatus: Map<number, "in" | "excluded">;
  selectedDetail: TaxonomyResponseDto | null;
  debouncedSearch: TaxonSearchFn & { cancel: () => void };
  cacheDetail: (tax: TaxonomyResponseDto) => void;
  cardLabel: string;
  searchLabel: string;
  showExcludedWarning: boolean;
};

const taxonPath = (tax: TaxonomyResponseDto): string =>
  [tax.class_, tax.order, tax.family].filter(Boolean).join(" > ");

const RichTaxonPicker: React.FC<RichTaxonPickerProps> = ({
  value,
  onChange,
  disabled,
  errors,
  taxonStatus,
  selectedDetail,
  debouncedSearch,
  cacheDetail,
  cardLabel,
  searchLabel,
  showExcludedWarning,
}) => {
  const { t } = useTranslation();
  const searchInputId = useId();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<TaxonomyResponseDto[]>([]);
  const [searching, setSearching] = useState(false);
  // Guards against out-of-order responses: an earlier query's request can
  // resolve after a later one's, and would otherwise overwrite `results`
  // with stale data. Only a callback whose term still matches the latest
  // issued query is allowed to update state.
  const latestTermRef = useRef("");
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const term = query.trim();
    latestTermRef.current = term;
    if (term.length < 3) {
      debouncedSearch.cancel();
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    debouncedSearch(term, (found) => {
      if (latestTermRef.current !== term) return;
      setSearching(false);
      setResults(found);
    });
  }, [query, debouncedSearch]);

  const selectResult = (tax: TaxonomyResponseDto) => {
    cacheDetail(tax);
    onChange(tax.id);
    debouncedSearch.cancel();
    setQuery("");
    setResults([]);
  };

  /**
   * Escape while the results list is showing closes the list, not the dialog around the
   * picker (AKL-883 criterion 10). The list is a column of buttons, not a `role="listbox"`,
   * so `ModalDialog` cannot see it and would otherwise close on the first Escape.
   *
   * It clears the typed query along with the results: keeping the query would leave "No
   * matching species found" under it, which is not true. Focus goes back to the search
   * input, including when the Escape was pressed on a result button that is about to go.
   *
   * `stopPropagation` on the React event also stops the native one at React's root, so
   * neither `ModalDialog`'s panel handler nor its `document` fallback sees this Escape. The
   * second matters: the result button is removed before a later listener would run, and the
   * fallback treats a key whose target has left the panel as a key from outside it. With no
   * results showing the key is left alone, and the dialog's own Escape applies.
   * @param event the key pressed on a control inside the picker
   */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Escape" || results.length === 0) return;
    event.stopPropagation();
    event.preventDefault();
    // `cancel` only stops a search that has not started. Invalidate the term now, not when
    // the query effect next runs, so a request already in flight cannot bring the results
    // back (Copilot, PR #835 review).
    debouncedSearch.cancel();
    latestTermRef.current = "";
    setSearching(false);
    setQuery("");
    setResults([]);
    searchInputRef.current?.focus();
  };

  const selectedStatus = value != null ? taxonStatus.get(value) : undefined;

  return (
    // Not an interactive element: the key handler only catches Escape bubbling up from the
    // search input and result buttons inside it.
    <div className="flex flex-col gap-3" onKeyDown={handleKeyDown}>
      <div className="flex flex-col gap-1">
        <span className={tailwindClassnames.textInputLabel}>{cardLabel}</span>
        <div className="border border-sky-blue-dark/40 dark:border-sky-blue/30 bg-sky-blue/5 dark:bg-sky-blue-dark/10 rounded-lg p-4">
          <div className="flex flex-row items-start justify-between gap-2">
            {selectedDetail ? (
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-primary-off-black dark:text-primary-off-white">
                    {selectedDetail.common_name_english ||
                      selectedDetail.scientific_name}
                  </span>
                  {selectedDetail.taxon_level && (
                    <span className={levelBadgeClass}>
                      {selectedDetail.taxon_level.toUpperCase()}
                    </span>
                  )}
                </div>
                {selectedDetail.scientific_name && (
                  <p className="italic text-sm text-greyed-text mt-0.5">
                    {selectedDetail.scientific_name}
                    {selectedDetail.authority &&
                      ` (${selectedDetail.authority})`}
                  </p>
                )}
                {taxonPath(selectedDetail) && (
                  <p className="text-xs text-greyed-text mt-0.5">
                    {taxonPath(selectedDetail)}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-greyed-text">
                {t("Search below to pick a species")}
              </p>
            )}
          </div>
        </div>
      </div>

      {!disabled && (
        <>
          <div className="flex flex-col gap-1">
            <label
              htmlFor={searchInputId}
              className={tailwindClassnames.textInputLabel}
            >
              {searchLabel}
            </label>
            <input
              ref={searchInputRef}
              id={searchInputId}
              type="text"
              className={tailwindClassnames.detailsPageTextInput}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("Type to search species...")}
            />
          </div>

          {query.trim().length > 0 && query.trim().length < 3 && (
            <p className="text-xs text-greyed-text">
              {t("Type at least 3 characters to search")}
            </p>
          )}

          {searching && (
            <p className="flex items-center gap-2 text-xs text-greyed-text">
              <BeatLoader
                size={5}
                color={tailwindClassnames.stormBlue}
                loading={true}
              />
              {t("Searching...")}
            </p>
          )}

          {!searching && query.trim().length >= 3 && results.length === 0 && (
            <p className="text-xs text-greyed-text">
              {t("No matching species found")}
            </p>
          )}

          {results.length > 0 && (
            <div className="border border-off-white-dark dark:border-off-black-light rounded divide-y divide-off-white-dark dark:divide-off-black-light">
              {results.map((tax) => {
                const status = taxonStatus.get(tax.id);
                const isSelected = tax.id === value;
                return (
                  <button
                    key={tax.id}
                    type="button"
                    onClick={() => selectResult(tax)}
                    className={
                      isSelected && status === "excluded"
                        ? "w-full text-left px-4 py-3 flex items-center justify-between gap-3 border-l-4 border-primary-orange bg-primary-orange-light/20 dark:bg-primary-orange-dark/20"
                        : isSelected
                          ? "w-full text-left px-4 py-3 flex items-center justify-between gap-3 bg-sky-blue/10"
                          : "w-full text-left px-4 py-3 flex items-center justify-between gap-3 hover:bg-off-white-dark dark:hover:bg-off-black-light"
                    }
                  >
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-primary-off-black dark:text-primary-off-white">
                          {tax.common_name_english || tax.scientific_name}
                        </span>
                        {tax.taxon_level && (
                          <span className={levelBadgeClass}>
                            {tax.taxon_level.toUpperCase()}
                          </span>
                        )}
                      </div>
                      {tax.scientific_name && (
                        <p className="italic text-xs text-greyed-text mt-0.5">
                          {tax.scientific_name}
                          {tax.authority && ` (${tax.authority})`}
                        </p>
                      )}
                    </div>
                    {status && (
                      <span
                        className={
                          status === "excluded"
                            ? "text-xs text-primary-orange-dark dark:text-primary-orange shrink-0"
                            : "text-xs text-greyed-text shrink-0"
                        }
                      >
                        {status === "excluded"
                          ? t("excluded")
                          : t("in species list")}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {showExcludedWarning && selectedStatus === "excluded" && (
        <div className="border-l-4 border-primary-orange bg-primary-orange-light/10 dark:bg-primary-orange-dark/10 rounded px-4 py-3 flex gap-3 items-start">
          <span className="shrink-0 mt-0.5">
            <AttentionIcon />
          </span>
          <p className="text-sm text-off-black-light dark:text-off-white-dark">
            {t(
              "{{name}} is currently excluded via another label in this project — exclusion applies per label, not per species, so pointing this label there won't change that. This label's own predictions will still reach reviewers unless you also set its status to Excluded below.",
              {
                name:
                  selectedDetail?.common_name_english ||
                  selectedDetail?.scientific_name ||
                  "",
              },
            )}
          </p>
        </div>
      )}

      {errors && (
        <p className="bg-primary-orange-light rounded text-primary-orange-dark text-sm px-3 py-2">
          {errors}
        </p>
      )}
    </div>
  );
};

const levelBadgeClass =
  "rounded text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 border border-off-white-dark dark:border-off-black-light text-off-black-light dark:text-off-white-dark shrink-0";

export default ProjectTaxonPicker;
